import React from 'react'
import moment from 'moment'
import { PROGRAM_CODES } from './constants'

RegExp.escape = function(s) {
  const str = JSON.stringify(s)
  const escaped = str.substring(1, str.length - 1)
  // eslint-disable-next-line
  return escaped.replace(/[-\/^$*+?.()|[\]{}]/g, '\\$&')
}

export const emailRegex = /^[a-zA-Z0-9!`#$%&'*+/=?^_‘{|}~-]+(?:\.[a-zA-Z0-9!`#$%&'*+/=?^_‘{|}~-]+)*@(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?$/

// SkuCode to Display hash
export const SkuCodeToDisplay = {
  ADL: { productCode: 'SELA', hierarchy: 3 },
  BPU: { productCode: 'BPU', hierarchy: 4 },
  CPU: { productCode: 'CPU', hierarchy: 5 },
  K5P: { productCode: 'K5', hierarchy: 1 },
  K8P: { productCode: 'K8', hierarchy: 2 },
  K8PF: { productCode: 'K8', hierarchy: 2 },
  MSP: { productCode: 'K8', hierarchy: 2 },
  HSP: { productCode: 'HSP', hierarchy: 6 },
}

// ===== UNIFIED ROLE DEFINITIONS =====
// Single source of truth for all role metadata
const RoleDefinitions = {
  teacher: {
    id: 'Teacher',
    name: 'Teacher',
    friendlyName: 'Teacher',
    role: 'teacher',
    article: 'a',
    plural: 's',
  },
  admin: {
    id: 'Admin',
    name: 'Admin',
    friendlyName: 'Administrator',
    role: 'admin',
    article: 'an',
    plural: 's',
  },
  leaderTeam: {
    id: 'Leader Team',
    name: 'Leader Team',
    friendlyName: 'Leader Team',
    role: 'Leader Team',
    article: 'a',
    plural: '',
  },
  coordinator: {
    id: 'Coordinator',
    name: 'Coordinator',
    friendlyName: 'Coordinator',
    role: 'coordinator',
    article: 'a',
    plural: 's',
  },
  family: {
    id: 'Family',
    name: 'Family',
    friendlyName: 'Family',
    role: 'family',
    article: 'a',
    plural: '',
  },
}

// Destructure for convenient access
const { admin, coordinator, family, leaderTeam, teacher } = RoleDefinitions

// AccessLevel: lookup by role name (both cases for backward compatibility)
export const AccessLevel = {
  teacher,
  Teacher: teacher,
  admin,
  Admin: admin,
  'leader team': leaderTeam,
  'Leader Team': leaderTeam,
  coordinator,
  Coordinator: coordinator,
}

// ===== PROGRAM-SPECIFIC ROLE DESCRIPTIONS =====
// Descriptions vary by context (classroom, training, SELA, add-user modal)

const RoleDescriptions = {
  classroom: {
    teacher: 'Can create classes and teach lessons',
    admin:
      'Can add, remove and change users, access progress reports, create classes and teach lessons',
  },
  training: {
    teacher: 'Can access online training',
    admin:
      'Can add, remove, and change users, view reports, and access online training',
  },
  sela: {
    admin:
      'Can add, remove, and change users, view reports, and access online training',
    leaderTeam: 'Can view Leader Team and Staff tracks',
    teacher: 'Can access online training',
  },
  // Add-user modal descriptions (arrays of capabilities)
  addUserDefault: {
    teacher: ['Can view and teach lessons', 'Can access online training'],
    admin: [
      'Can manage users',
      'Can view reports',
      'Can view and teach lessons',
      'Can access online training',
    ],
  },
  addUserSela: {
    teacher: [
      'Can view and teach lessons',
      'Can access microlearnings',
      'Can access online training',
    ],
    leaderTeam: [
      'Can view and teach lessons',
      'Can access microlearnings',
      'Can access Leader supports',
      'Can access online training',
    ],
    admin: [
      'Can manage users',
      'Can view reports',
      'Can view and teach lessons',
      'Can access microlearnings',
      'Can access Leader supports',
      'Can access online training',
    ],
  },
}

// Helper to build role info for a program
const buildRoleInfo = (roleKey, descriptionKey) => ({
  description: RoleDescriptions[descriptionKey][roleKey],
  id: RoleDefinitions[roleKey].id,
  name: RoleDefinitions[roleKey].friendlyName,
})

// Helper to build add-user role info
const buildAddUserRoleInfo = (roleKey, descriptionKey) => ({
  description: RoleDescriptions[descriptionKey][roleKey],
  id: RoleDefinitions[roleKey].id,
  role: RoleDefinitions[roleKey].friendlyName,
})

// RolesInfo: program-specific role configurations for role info display
export const RolesInfo = {
  K8P: [
    buildRoleInfo('teacher', 'classroom'),
    buildRoleInfo('admin', 'classroom'),
  ],
  MSP: [
    buildRoleInfo('teacher', 'classroom'),
    buildRoleInfo('admin', 'classroom'),
  ],
  SSP: [
    { id: teacher.id, name: teacher.name, description: 'TBD' },
    { id: coordinator.id, name: coordinator.name, description: 'TBD' },
    { id: family.id, name: family.name, description: 'TBD' },
  ],
  ADL: [
    buildRoleInfo('teacher', 'training'),
    buildRoleInfo('leaderTeam', 'sela'),
    buildRoleInfo('admin', 'training'),
  ],
  HSP: [
    buildRoleInfo('teacher', 'training'),
    buildRoleInfo('admin', 'training'),
  ],
  OST: [{ id: teacher.id, name: teacher.name, description: 'TBD' }],
  BPU: [
    buildRoleInfo('teacher', 'training'),
    buildRoleInfo('admin', 'training'),
  ],
  CPU: [
    buildRoleInfo('teacher', 'training'),
    buildRoleInfo('admin', 'training'),
  ],
}

// AddUsersRolesInfo: role configurations for add-user modal
export const AddUsersRolesInfo = {
  ADL: [
    buildAddUserRoleInfo('teacher', 'addUserSela'),
    buildAddUserRoleInfo('leaderTeam', 'addUserSela'),
    buildAddUserRoleInfo('admin', 'addUserSela'),
  ],
  Default: [
    buildAddUserRoleInfo('teacher', 'addUserDefault'),
    buildAddUserRoleInfo('admin', 'addUserDefault'),
  ],
}

/**
 * Collects all unique roles from a site's licenses/assets.
 * Merges descriptions when the same role appears across multiple programs.
 * @param {Object} site - The site object containing netSuiteAssets
 * @returns {Array} Array of role objects with merged descriptions
 */
export const getRoleDescriptionToolTips = site => {
  const licenses = site?.netSuiteAssets || []
  const allRolesMap = new Map()

  for (const license of licenses) {
    const programCodes = license?.product?.programCodes || []
    for (const code of programCodes) {
      const roles = RolesInfo[code] || []
      for (const role of roles) {
        if (!allRolesMap.has(role.id)) {
          allRolesMap.set(role.id, {
            ...role,
            descriptions: [role.description],
          })
        } else {
          const existing = allRolesMap.get(role.id)
          if (!existing.descriptions.includes(role.description)) {
            existing.descriptions.push(role.description)
          }
        }
      }
    }
  }

  return Array.from(allRolesMap.values()).map(role => ({
    id: role.id,
    name: role.name,
    description: role.descriptions.join('\n'),
  }))
}

export const InvitationStatus = {
  Invited: 'Invited',
  Cancelled: 'Cancelled',
  Active: 'Active',
  Removed: 'Removed',
  None: null,
}

export const AVAILABLE_TRAINING_REPORTS = ['CPU', 'BPU']

export const AVAILABLE_LESSON_REPORTS = ['K8P', 'MSP', 'ADL', 'K12P']

export const sortArrayBy = (array, key) => {
  if (array == null) {
    return
  }
  return array.sort((a, b) => {
    const valueA = a[key]?.toLowerCase()
    const valueB = b[key]?.toLowerCase()
    if (valueA < valueB) {
      return -1
    }
    if (valueA > valueB) {
      return 1
    }
    return 0
  })
}

export const findLicenseUser = (license, email) => {
  const licenseUsers = license.users.filter(user => user.email === email)
  if (licenseUsers.length > 0) return licenseUsers[0]
}

export const displayDate = (date, format = 'MMMM DD, YYYY') => {
  if (!date) {
    return ''
  }
  const formattedDate = moment(date).format(format)
  return formattedDate
}

export const isTrainingReportAvailable = (
  asset,
  bpuTrainingData,
  cpuTrainingData,
) => {
  const programCodes = asset?.product?.programCodes || []
  const isCorrectSkuCode = programCodes.some(code =>
    AVAILABLE_TRAINING_REPORTS.includes(code),
  )
  let trainingData
  if (programCodes.includes('BPU')) trainingData = bpuTrainingData
  if (programCodes.includes('CPU')) trainingData = cpuTrainingData
  return {
    showTrainingReportDownload: isCorrectSkuCode && asset?.isActive,
    trainingData,
  }
}

export const isLessonReportAvailable = license => {
  const programCodes = license?.product?.programCodes || []
  const isCorrectProgramCode = programCodes.some(code =>
    AVAILABLE_LESSON_REPORTS.includes(code),
  )
  return (
    isCorrectProgramCode && license?.isActive && license?.status !== 'Expired'
  )
}

export const displayRoles = roles => {
  return [...roles]
    .map(role => role.toLowerCase())
    .map(role => AccessLevel[role].friendlyName)
    .sort()
    .join(', ')
}

export const filterCanceledUsers = (licenseUsers = []) =>
  licenseUsers?.filter(
    ({ status }) =>
      status !== InvitationStatus.Cancelled &&
      status !== InvitationStatus.Removed,
  )

export const displayAdmins = (row, currentUserEmail) => {
  if (row == null || row.admins == null || row.admins.length === 0) return '-'

  const currentUserInfo = row.admins.find(
    admin => admin.email === currentUserEmail,
  )
  const firstTwoAdmins = row.admins.slice(0, 2)
  let hasCurrentUser = firstTwoAdmins.includes(currentUserInfo)

  if (currentUserInfo && !hasCurrentUser) firstTwoAdmins[0] = currentUserInfo

  const adminsToDisplay = firstTwoAdmins.map(admin => {
    return admin.firstName
      ? `${admin.firstName} ${admin.lastName}`
      : admin.email
  })

  return adminsToDisplay?.join(', ') || '-'
}

export const validateEmail = email => {
  // eslint-disable-next-line
  var re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
  return re.test(String(email).toLowerCase())
}

export const extractExpirationInfo = (obj, format = 'MMMM DD, YYYY') => {
  const startDate = obj.startDate ? moment(obj.startDate) : null
  const cancellationDate = obj.canceledDate
    ? moment(obj.canceledDate)
    : undefined
  const expirationDate = obj.expirationDate
    ? moment(obj.expirationDate)
    : undefined
  const date =
    obj.canceledDate && cancellationDate < expirationDate
      ? cancellationDate
      : expirationDate
  const now = moment.utc()
  const isExpired = date <= now
  const daysLeft = Math.max(0, date && date.diff(now, 'days'))
  let icon = ''
  if (date <= now) icon = <i className="fa fa-times-circle text-danger" />
  else if (daysLeft <= 90)
    icon = <i className="fa fa-exclamation-triangle text-warning" />

  return {
    now: now,
    startDate: {
      native: obj.startDate,
      moment: startDate,
      formatted: obj.startDate ? startDate.format(format) : null,
    },
    expirationDate: {
      native: obj.expirationDate,
      moment: expirationDate,
      formatted: obj.expirationDate ? expirationDate.format(format) : null,
    },
    canceledDate: {
      native: obj.canceledDate,
      moment: obj.canceledDate ? cancellationDate : null,
      formatted: obj.canceledDate ? cancellationDate.format(format) : null,
    },
    effectiveEndDate: {
      moment: date,
      formatted: date && date.format(format),
    },
    format: format,
    isExpired: isExpired,
    isExpiringSoon: !isExpired && daysLeft <= 90,
    expirationWarningThresholdDays: 90,
    daysLeft: daysLeft,
    label: isExpired ? 'Expired on ' : 'Expires on ',
    icon: icon,
  }
}

export const capitalize = string =>
  string.replace(/(\b[a-z](?!\s))/g, char => char.toUpperCase())

export const inputModeType = {
  single: 'single',
  bulk: 'bulk',
}

export const pluralize = (number, word, plural = word + 's') => {
  const isSingular = [1, -1].includes(Number(number))
  return isSingular ? word : plural
}

export const setCookie = (cname, cvalue, exdays) => {
  const d = new Date()
  d.setTime(d.getTime() + exdays * 24 * 60 * 60 * 1000)
  const expires = `expires=${d.toUTCString()}`
  document.cookie = `${cname}=${cvalue};${expires};path=/`
}

const hasSela = licenses => {
  const programCodes = licenses.reduce((acc, license) => {
    return acc.concat(license.product.programCodes)
  }, [])
  return programCodes.indexOf('ADL') > -1
}

const siteRoles = [admin, leaderTeam, teacher]

export const getSitesApplicableRoles = site => {
  const licenses = site.licenses || site.netSuiteAssets
  if (hasSela(licenses)) {
    return siteRoles
  }

  return siteRoles.filter(role => role.name !== leaderTeam.friendlyName)
}

export const UpdateSiteUserNotifications = {
  grantNewRole: {
    operationName: 'grantNewRole',
    successMessage: ({ email, role }) =>
      `You've successfully granted the ${role} role to ${email}.`,
    errorMessage: ({ email, role }) =>
      `We weren't able to grant the ${role} role to ${email}. Please try again later.`,
  },
  removeUser: {
    operationName: 'removeUser',
    successMessage: ({ email }) => `You've successfully removed ${email}.`,
    errorMessage: ({ email }) =>
      `We weren't able to remove ${email}. Please try again later.`,
  },
}

export const siteLicenseChecker = site => {
  const programCodeChecks = {
    hasK8License: false,
    hasSelaLicense: false,
    hasBpuLicense: false,
    hasCpuLicense: false,
  }

  const { licenses = [] } = site

  licenses.forEach(license => {
    const { programCodes = [] } = license.product || {}

    // Check if license is active - support both isActive/status and isExpired patterns
    const isActive = license?.isActive && license?.status !== 'Expired'

    if (isActive) {
      programCodes.forEach(code => {
        if (PROGRAM_CODES.k8.includes(code)) {
          programCodeChecks.hasK8License = true
        }
        if (PROGRAM_CODES.sela.includes(code)) {
          programCodeChecks.hasSelaLicense = true
        }
        if (PROGRAM_CODES.bpu.includes(code)) {
          programCodeChecks.hasBpuLicense = true
        }
        if (PROGRAM_CODES.cpu.includes(code)) {
          programCodeChecks.hasCpuLicense = true
        }
      })
    }
  })

  return programCodeChecks
}
